import DotGray4Svg from "@/src/assets/icons/icon-dot-gray4.svg";
import DotGray7Svg from "@/src/assets/icons/icon-dot-gray7.svg";
import AlertDialog from "@/src/components/atom/AlertDialog";
import { Button } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import Typo from "@/src/components/atom/Typo";
import BookMarkStatus from "@/src/components/molecule/BookMarkStatus";
import TabItem from "@/src/components/molecule/TabItem";
import UnauthorizedDescription from "@/src/components/molecule/UnauthorizedDescription";
import EmailHistoryDialog from "@/src/components/organism/EmailHistoryDialog";
import NoRegisteredSignatureAlertDialog from "@/src/components/organism/NoRegisteredSignatureAlertDialog";
import SignatureRequestAlertDialog from "@/src/components/organism/SignatureRequestAlertDialog";
import ImporterMainLayout from "@/src/components/template/Layout/importer/ImporterMainLayout";
import IMPORTER_PRIVATE_PATH from "@/src/routes/importer/path";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import useAlert from "@/src/hooks/useAlert";
import useContentLoading from "@/src/hooks/useContentLoading";
import { useGetSessionQuery } from "@/src/store/apis/auth";
import { useGetCommonCodeViaCodeNameQuery } from "@/src/store/apis/common";
import { PartialCommonCodeItemDto } from "@/src/store/apis/common/interface";
import {
  useConfirmImporterPoMutation,
  useConfirmImporterScMutation,
  useGetImporterContractDetailBookmarkQuery,
  useGetImporterContractShipmentSummaryInfoQuery,
  useLazyGetImporterContractDetailQuery,
  useLazyGetImporterContractSignatureHistoriesQuery,
  useLazyGetImporterPoHistoriesQuery,
  useLazyGetImporterScHistoriesQuery,
  useRejectImporterScMutation,
  useUpdateContractDetailStatusMutation,
  useUpdateImporterContractDetailBookmarkMutation,
} from "@/src/store/apis/contracts/contractDetail";
import { ContractStatusType } from "@/src/store/apis/contracts/interface";
import { useGetExportersQuery } from "@/src/store/apis/corporate/company";
import { useLazyGetEmailShareHistoriesQuery } from "@/src/store/apis/emailShareHistories";
import { useGetImporterContractSignatureHistoriesQuery } from "@/src/store/apis/contracts/contractDetail";
import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import { isUndefined } from "@/src/utils/is";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { styled } from "styled-components";
import ContractFilesTab from "./components/detail/ContractFilesTab";
import ContractInformationTab from "./components/detail/ContractInformationTab";
import ContractHistoryCard from "@/src/pages/exporter/Contract/components/detail/ContractHistoryCard";
import CancelContractAlertDialog from "./components/detail/dialog/CancelContractAlertDialog";
import EmailDialog from "./components/detail/dialog/EmailDialog";
import RedoAlertDialog from "./components/detail/dialog/RedoAlertDialog";
import RegisterSignDialog from "./components/detail/dialog/RegisterSignDialog";
import RejectReasonDialog from "./components/detail/dialog/RejectReasonDialog";
import ProcessingTab from "./components/detail/ProcessingTab";
import StoreTab from "./components/detail/StoreTab";
import renderContractStatusBadge from "./utils/renderContractStatusBadge";
import SignatureSelectDialog from "@/src/components/organism/SignatureSelectDialog";
import { useGetBuyersQuery } from "@/src/store/apis/client/buyer";
import { getContractCurrentVersion } from "@/src/utils/getContractCurrentVersion";
import { useCompanyLocalDate } from "@/src/hooks/useCompanyLocalDate";
import dayjs from "dayjs";
import EmailDropdown from "@/src/components/molecule/EmailDropdown";
import {
  determineContractFlow,
  determineContractStage,
  getHistoryDocumentType,
  isLeadRole as checkIsLeadRole,
  isCurrentLeadRole,
} from "@/src/utils/contractHistory";
import { getContractNoticeType } from "@/src/utils/getContractNoticeType";
import { ContractNoticeType } from "@/src/components/molecule/ContractStatusNotice";
import { SignatureFullscreenViewer } from "@/src/components/organism/SignatureFullscreenViewer";
import RejectReasonViewModal from "@/src/components/organism/RejectReasonViewModal";

enum DialogState {
  NULL,
  SEND_EMAIL,
  SENT_HISTORY,
  REJECT_SC_REASON,
  REGISTER_SIGN,
  CONTRACT_HISTORY,
  SIGNATURE_FULLSCREEN,
  VIEW_REJECT_REASON,
}

enum AlertDialogState {
  NULL,
  CANCEL,
  REDO,
  SIGN_SC,
  REJECT_SC,
  CONFIRM_REQUEST_PO,
  REGISTER_SIGN,
  SIGNATURE_REQUEST,
  SIGNATURE_SELECT_SC,
  SIGNATURE_SELECT_PO,
}

type TabType = "contractInformation" | "processing" | "store" | "files";

const TAB_LIST: {
  id: TabType;
  label: string;
}[] = [
  {
    id: "contractInformation",
    label: "common:importerContractInformation",
  },
  {
    id: "processing",
    label: "common:processing",
  },
  {
    id: "store",
    label: "common:receipt",
  },
  {
    id: "files",
    label: "common:files",
  },
];

const mainCategoryEmptyArray: PartialCommonCodeItemDto[] = [];

const ImporterContractDetailPage = () => {
  const { t } = useTranslation();
  const alert = useAlert();
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [tab, setTab] = useState<TabType>(() => {
    const tabParam = searchParams.get("selectTab");
    return (tabParam as TabType) || "contractInformation";
  });
  const [poVersion, setPoVersion] = useState<number>();
  const [scVersion, setScVersion] = useState<number>();
  const [processingTab, setProcessingTab] = useState<
    ("booking" | "loading" | "shipment")[]
  >(["booking"]);
  const [selectedProcessingTab, setSelectedProcessingTab] = useState<
    "booking" | "loading" | "shipment"
  >();
  const [selectedProcessingItem, setSelectedProcessingItem] =
    useState<number>();
  const { handleContentLoadingOff, handleContentLoadingOn } =
    useContentLoading();

  // API
  const { mainCategoryList } = useGetCommonCodeViaCodeNameQuery(
    {
      codeName: "MAIN_CATEGORY",
    },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isError || isFetching || isUndefined(currentData);
        const isStable = !isUnstable;

        return {
          mainCategoryList: isStable
            ? (currentData ?? mainCategoryEmptyArray)
            : mainCategoryEmptyArray,
        };
      },
    },
  );
  const { currentData: buyers } = useGetBuyersQuery({ isActivated: true });

  const { userId, exporterUserType, userMainCategory, isUserFetching } =
    useGetSessionQuery(undefined, {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isError || isFetching || isUndefined(currentData);
        const isStable = !isUnstable;

        return {
          isUserFetching: isFetching,
          userId: currentData?.row.id,
          userMainCategory: isStable
            ? currentData.row.mainCategoryCodeItemNames
            : [],
          exporterUserType: currentData?.row.exporterUserType,
        };
      },
    });

  const { hasSign, isTwoSign, signatureManagement } = useGetExportersQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isError }) => {
        const isUnstable = isError || isUndefined(currentData);
        const isStable = !isUnstable;
        return {
          hasSign: isStable
            ? currentData.signatureManagement.simpleExporterSignatureMedias
                ?.length > 0
            : false,
          isTwoSign: isStable
            ? currentData.signatureManagement.simpleExporterSignatureMedias
                ?.length === 2
            : false,
          signatureManagement: isStable
            ? currentData.signatureManagement.simpleExporterSignatureMedias
            : [],
        };
      },
    },
  );

  const { balanceOfContractV2, totalShippingWeightV2, percentOfShipment } =
    useGetImporterContractShipmentSummaryInfoQuery(
      { id: Number(params.id) },
      {
        refetchOnMountOrArgChange: true,
        selectFromResult: ({ currentData, isError }) => {
          const isErrorAndUndefined = isError || currentData === undefined;

          return {
            balanceOfContractV2: !isErrorAndUndefined
              ? (currentData?.balanceOfContractV2 ?? "-")
              : "-",
            totalShippingWeightV2: !isErrorAndUndefined
              ? (currentData?.totalShippingWeightV2 ?? "-")
              : "-",
            percentOfShipment:
              !isErrorAndUndefined && currentData.percentOfShipment
                ? Number(currentData?.percentOfShipment)
                : 0,
          };
        },
      },
    );
  const [
    getContractDetail,
    {
      clientName,
      isContractError,
      contractWriterId,
      contractStatus,
      contractUpdatedAt,
      contractCreatedAt,
      contractLastEditorName,
      contractWriterName,
      poNo,
      scNo,
      isContractDetailFetching,
      isContractUnstable,
      isContractStable,
      contractMainCategory,
      contractMainCategoryForImporter,
      signatureStatus,
      sellerId,
      contractStatusInfo,
    },
  ] = useLazyGetImporterContractDetailQuery({
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isUndefined(currentData) || isError || isFetching;
      const isStable = !isUnstable;

      return {
        sellerId: isStable ? currentData.exporterId : undefined,
        clientName: isStable ? currentData.buyerName : "",
        isContractUnstable: isUnstable,
        isContractStable: isStable,
        isContractError: isError,
        isContractDetailFetching: isFetching,
        contractWriterId: isStable ? currentData.contractWriterId : null,
        contractStatus: isStable ? currentData.contractStatus : "NOT_LINKED",
        contractUpdatedAt: isStable ? currentData.updatedAt : "",
        contractCreatedAt: isStable ? currentData.createdAt : "",
        contractLastEditorName: isStable
          ? (currentData.contractLastEditorName ?? "-")
          : "-",
        contractWriterName: isStable
          ? (currentData.contractWriterName ?? "-")
          : "-",
        poNo: isStable ? currentData.poNo : undefined,
        scNo: isStable ? currentData.scNo : undefined,
        scSimpleMedia: isStable ? currentData?.scSimpleMedia : null,
        contractMainCategory: isStable ? (currentData.mainCategory ?? "") : "",
        contractMainCategoryForImporter: isStable
          ? (currentData.mainCategoryForImporter ?? "")
          : "",
        signatureStatus: isStable
          ? currentData.signatureStatus || "DRAFT"
          : "DRAFT",
        contractStatusInfo: isStable ? currentData.contractStatusInfo : null,
      };
    },
  });

  // Contract Signature Histories API
  const { data: signatureHistories, isLoading: isSignatureHistoriesLoading } =
    useGetImporterContractSignatureHistoriesQuery(
      { id: Number(params.id) },
      {
        skip: !params.id,
      },
    );

  const { isBookmarked } = useGetImporterContractDetailBookmarkQuery(
    { id: Number(params.id) },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData }) => {
        return { isBookmarked: currentData?.isBookmarked };
      },
    },
  );
  const [editContractBookmark] =
    useUpdateImporterContractDetailBookmarkMutation();
  const [contractStatusChange] = useUpdateContractDetailStatusMutation();
  const [
    getContractSharedHistories,
    {
      count,
      emailHistoryList,
      isEmailShareHistoriesFetching,
      isEmailShareHistoriesError,
    },
  ] = useLazyGetEmailShareHistoriesQuery({
    selectFromResult: ({ currentData, isFetching, isError }) => {
      const isUnstable = isUndefined(currentData) || isError || isFetching;

      return {
        count: !isUnstable ? currentData.count : 0,
        emailHistoryList: !isUnstable ? currentData.rows : undefined,
        isEmailShareHistoriesError: isError,
        isEmailShareHistoriesFetching: isFetching,
      };
    },
  });
  const [
    getContractSignatureHistories,
    {
      isOnlyReceivedSc,
      isPending,
      isExporterScSigned,
      isImporterScSigned,
      hasLinkedBefore,
      previousSignature,
      currentSignature,
      isSentPO,
      isExporterPoSigned,
      isImporterPoSigned,
    },
  ] = useLazyGetImporterContractSignatureHistoriesQuery({
    selectFromResult: ({ currentData }) => ({
      previousSignature: currentData?.rows[1],
      currentSignature: currentData?.rows[0],
      isOnlyReceivedSc:
        currentData?.rows[0].signatureStatus === "PENDING" ||
        (currentData?.rows[0].signatureStatus === "SENT" &&
          currentData?.rows[0].isExporterSignedSC),
      isPending: currentData?.rows[0].signatureStatus === "PENDING",
      isExporterScSigned: !!currentData?.rows[0].isExporterSignedSC,
      isImporterScSigned: !!currentData?.rows[0].isImporterSignedSC,
      isExporterPoSigned: !!currentData?.rows[0].isExporterSignedPO,
      hasLinkedBefore: currentData?.rows.some(
        ({
          isExporterSignedSC,
          isImporterSignedSC,
          isExporterSignedPO,
          isImporterSignedPO,
        }) =>
          isExporterSignedSC &&
          isImporterSignedSC &&
          isExporterSignedPO &&
          isImporterSignedPO,
      ),
      isSentPO: currentData?.rows[0].isImporterSignedPO,
      isImporterPoSigned: !!currentData?.rows[0].isImporterSignedPO,
    }),
  });

  const [getImporterPoHistories, { poHistories, isZeroPo }] =
    useLazyGetImporterPoHistoriesQuery({
      selectFromResult: ({ currentData, isError }) => {
        return {
          poHistories: currentData?.rows ?? [],
          isZeroPo: isError,
        };
      },
    });
  const [getImporterScHistories] = useLazyGetImporterScHistoriesQuery();

  const [confirmImporterPo] = useConfirmImporterPoMutation();
  const [confirmImporterSc] = useConfirmImporterScMutation();
  const [rejectSc] = useRejectImporterScMutation();

  // State
  const [filesTab, setFilesTab] = useState<"list" | "gallery">("list");
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);
  const [alertDialogState, setAlertDialogState] = useState<AlertDialogState>(
    AlertDialogState.NULL,
  );
  const [selectedSignatureId, setSelectedSignatureId] = useState<number | null>(
    null,
  );
  const [signatureType, setSignatureType] = useState<"PO" | "SC" | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showRegisterSignDialog, setShowRegisterSignDialog] = useState(false);

  const contractCreatedDate = useCompanyLocalDate({
    date: contractCreatedAt,
    format: DATE_FORMAT_STRINGS.YYYY_MM_DD_HH_mm,
  });

  const contractUpdatedDate = useCompanyLocalDate({
    date: contractUpdatedAt,
    format: DATE_FORMAT_STRINGS.YYYY_MM_DD_HH_mm,
  });

  const defaultSignature = signatureManagement?.find((item) => item.isDefault);
  const defaultSignatureMediaId = defaultSignature?.signatureSimpleMedia.id;

  const mainCategoryItemName = mainCategoryList.find(
    (item) =>
      item.codeItemName ===
        contractMainCategory.toUpperCase().replace(/-/g, "_") ||
      item.codeItemName ===
        contractMainCategoryForImporter.toUpperCase().replace(/-/g, "_"),
  )?.codeItemName;

  // 매니저,중간관리자 카테고리 비교
  const isMainCategoryAuthorized =
    exporterUserType === "MANAGER" || exporterUserType === "MIDDLE_MANAGER"
      ? userMainCategory.includes(mainCategoryItemName as string)
      : true;

  const isMainCategoryUnauthorized = !isMainCategoryAuthorized;

  /**
   * 컨펌요청 disabled
   * - 모든 서명 완료
   * - 미연결 되어있는 거래처
   * - 버전업 이후 PO생성전
   */
  const _requestConfirmPoDisabled = useMemo(() => {
    if (signatureStatus === "LINKED" || isZeroPo) {
      return true;
    }

    if (
      signatureStatus === "APPROVED" &&
      getContractCurrentVersion(poHistories).currentVersion >= 2 &&
      getContractCurrentVersion(poHistories).currentVersionHistoryLength === 1
    ) {
      return true;
    }

    if (
      contractStatus === "NOT_LINKED" &&
      buyers?.data.find(
        ({ buyerListQueryResultDto }) =>
          sellerId === buyerListQueryResultDto.id,
      )?.buyerListQueryResultDto.linkedStatus !== "COMPLETED"
    ) {
      return true;
    }
  }, [
    signatureStatus,
    isZeroPo,
    poHistories,
    contractStatus,
    buyers?.data,
    sellerId,
  ]);

  /**
   * 연결되어있는 PO수정버튼 disable
   */
  const isLinkedPoEditDisabled = useMemo(() => {
    if (currentSignature?.signatureStatus === "SENT") {
      return true;
    }

    if (contractStatus === "PROCESSING") {
      if (
        currentSignature?.signatureStatus === "APPROVED" &&
        !currentSignature?.isImporterSignedPO
      ) {
        return false;
      }

      if (!hasLinkedBefore && !previousSignature?.isImporterSignedPO) {
        return true;
      }
    }
  }, [contractStatus, currentSignature, hasLinkedBefore, previousSignature]);

  /**
   * 수정 권한
   * - 기업관리자, 중간관리자, 작성자
   * - 일반 매내저는 메인카테고리가 포함된 사람
   */
  const isEditAuth =
    !isContractError &&
    (userId === contractWriterId || isMainCategoryAuthorized);

  /**
   * - 수정권한이 없는유저
   * - 거래처가 연결되어있지 않는 경우
   * - 서명 요청상태 Request
   * - 버전업 이후 서명 요청상태 SC를 Pending 한 경우
   * - 버전업 이전 PO를 요청, 거래처와 연결이 되어있지 않고, 서명이 모두 완료상태
   */
  const isDisabledEdit = useMemo(() => {
    if (
      !isEditAuth ||
      isLinkedPoEditDisabled ||
      signatureStatus === "REQUESTED"
    ) {
      return true;
    }

    if (
      hasLinkedBefore &&
      signatureStatus === "PENDING" &&
      previousSignature?.isExporterSignedSC &&
      !previousSignature?.isImporterSignedSC
    ) {
      return true;
    }

    return (
      !!isSentPO &&
      contractStatus !== "NOT_LINKED" &&
      signatureStatus !== "LINKED"
    );
  }, [
    isEditAuth,
    isLinkedPoEditDisabled,
    signatureStatus,
    hasLinkedBefore,
    previousSignature?.isImporterSignedSC,
    previousSignature?.isExporterSignedSC,
    isSentPO,
    contractStatus,
  ]);

  // Determine ContractNoticeType
  const contractNoticeType = useMemo((): ContractNoticeType | null => {
    // 필수 데이터가 아직 로드되지 않았으면 null 반환하여 깜빡임 방지
    if (!currentSignature || isSignatureHistoriesLoading) {
      return null;
    }

    // 반려 상태 판단 (백엔드 기준):
    // - signatureStatus가 'PENDING'이고
    // - rejectReason이 null이 아니고 빈 문자열이 아닌 경우
    const isRejected =
      currentSignature?.signatureStatus === "PENDING" &&
      currentSignature?.rejectReason !== null &&
      currentSignature?.rejectReason !== "";

    // Lead/Follow 판단: 현재 단계 기준으로 판단
    const isLeadRole = isCurrentLeadRole(signatureHistories?.rows, "IMPORTER");

    // 거래처 연결 여부 확인 (수입자 입장에서는 seller의 linkedStatus가 COMPLETED인지)
    // buyers API는 수입자 입장에서 seller 목록을 반환
    // 수입자 페이지에서는 clientName(buyerName)으로 매칭
    const isLinkedBuyer =
      clientName && buyers?.data
        ? buyers.data.find((buyer) => buyer.buyerName === clientName)
            ?.buyerListQueryResultDto.linkedStatus === "COMPLETED"
        : false;

    const noticeType = getContractNoticeType({
      contractStatus,
      signatureStatus: currentSignature?.signatureStatus || signatureStatus,
      poNo,
      scNo,
      isOnlyReceivedPo: false,
      isExporterPoSigned,
      isImporterPoSigned,
      isExporterScSigned,
      isImporterScSigned,
      isPending,
      isSentSc: !!isImporterScSigned,
      hasLinkedBefore,
      userRole: "IMPORTER",
      isLeadRole,
      isRejected,
      isLinkedBuyer,
      contractStatusInfo,
    });

    return noticeType;
  }, [
    contractStatus,
    signatureStatus,
    poNo,
    scNo,
    isPending,
    hasLinkedBefore,
    isZeroPo,
    isExporterPoSigned,
    isImporterPoSigned,
    isExporterScSigned,
    isImporterScSigned,
    currentSignature,
    signatureHistories,
    previousSignature,
    isContractStable,
    isSignatureHistoriesLoading,
    buyers?.data,
    contractStatusInfo,
  ]);

  const handleContractNoticeAction = useCallback(
    (actionType?: string) => {
      // VIEW_REJECTION_REASON 액션이 직접 전달된 경우
      if (actionType === "VIEW_REJECTION_REASON") {
        setDialogState(DialogState.VIEW_REJECT_REASON);
        return;
      }

      // 기존 contractNoticeType 기반 처리 (하위 호환성)
      if (!contractNoticeType) return;

      switch (contractNoticeType) {
        case "IM_LEAD_PO_DRAFT":
          navigate(
            `${IMPORTER_PRIVATE_PATH.CONTRACT_ADD}?id=${params.id}&clientCheck=false`,
          );
          break;
        case "IM_LEAD_PO_SIGNATURE_REQUIRED":
        case "IM_FOLLOW_PO_SIGNATURE_REQUIRED":
          // 서명 요청 버튼 클릭 시 무조건 Edit 페이지로 이동
          navigate(`${IMPORTER_PRIVATE_PATH.CONTRACT_EDIT_V2}/${params.id}`);
          break;
        case "IM_LEAD_SC_SIGNATURE_REQUIRED":
        case "IM_FOLLOW_SC_SIGNATURE_REQUIRED":
          // SC 계약서 보기 - 풀스크린 미리보기로 바로 이동
          (async () => {
            try {
              const contractId = Number(params.id);
              const scHistories = await getImporterScHistories({
                contractId,
              }).unwrap();
              const pdfUrl =
                scHistories?.rows?.[0]?.documentUrl ||
                scHistories?.rows?.[0]?.mediaFileUrl;
              if (pdfUrl) {
                setPdfUrl(pdfUrl);
                setSelectedSignatureId(null); // 서명 선택은 나중에
                setSignatureType("SC");
                setDialogState(DialogState.SIGNATURE_FULLSCREEN);
              }
            } catch (error) {
              console.error("Failed to fetch SC PDF URL:", error);
            }
          })();
          break;
        case "IM_LEAD_PO_REJECTED":
        case "IM_LEAD_SC_REJECTED":
        case "IM_FOLLOW_PO_REJECTED":
        case "IM_FOLLOW_SC_REJECTED":
          setDialogState(DialogState.VIEW_REJECT_REASON);
          break;
        case "IM_LEAD_CONTRACT_COMPLETE":
        case "IM_FOLLOW_CONTRACT_COMPLETE":
          // 계약서 보기 - 모달로 표시
          // ContractInformationTab의 setIsContractViewerOpen을 사용할 수 없으므로
          // 여기서는 아무 동작도 하지 않고, ContractProgressBar에 별도 핸들러 전달
          break;
        case "IM_FOLLOW_SC_RECEIVED":
          // SC 확인 - 계약 상세 페이지로 이동 (수정 모드)
          navigate(`${IMPORTER_PRIVATE_PATH.CONTRACT_EDIT_V2}/${params.id}`);
          break;
        case "IM_FOLLOW_PO_CREATE_REQUIRED":
          navigate(
            `${IMPORTER_PRIVATE_PATH.CONTRACT_ADD}?id=${params.id}&clientCheck=false`,
          );
          break;
      }
    },
    [contractNoticeType, params.id, navigate, getImporterScHistories],
  );

  // Handle signature requirement from contract viewer modal
  const handleSignatureRequired = useCallback(
    (type: "PO" | "SC") => {
      if (type === "PO") {
        if (isTwoSign) {
          setAlertDialogState(AlertDialogState.SIGNATURE_SELECT_PO);
        } else {
          setAlertDialogState(AlertDialogState.CONFIRM_REQUEST_PO);
        }
      } else if (type === "SC") {
        if (isTwoSign) {
          setAlertDialogState(AlertDialogState.SIGNATURE_SELECT_SC);
        } else {
          setAlertDialogState(AlertDialogState.SIGN_SC);
        }
      }
    },
    [isTwoSign],
  );

  const handleBookmarkClick = async () => {
    const bookmark = !isBookmarked;

    try {
      const bookmarkParams: { id: number; isBookmarked: boolean } = {
        id: Number(params.id),
        isBookmarked: bookmark,
      };

      await editContractBookmark(bookmarkParams).unwrap();
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const handleContractStatusChangeClick = async (
    type: "COMPLETE" | "PROCESSING",
  ) => {
    try {
      const contractParams = {
        contractStatus: type,
        contractId: Number(params.id),
      };

      await contractStatusChange(contractParams).unwrap();

      if (type === "COMPLETE") {
        alert.showAlert({
          type: "success",
          message: t("contract:importer.detail.alert.completeAlert"),
        });
      }

      if (type === "PROCESSING") {
        alert.showAlert({
          type: "success",
          message: t("contract:importer.detail.alert.redoAlert"),
        });
      }
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const handleDocument = async (data: {
    type: "confirmPo" | "confirmSc" | "rejectSc";
    rejectReason?: string;
    signatureMediaId?: number;
  }) => {
    try {
      handleContentLoadingOn();
      const id = Number(params.id);
      switch (data.type) {
        case "confirmPo":
          if (data.signatureMediaId) {
            await confirmImporterPo({
              id,
              signatureMediaId: data.signatureMediaId,
            }).unwrap();
          }
          alert.showAlert({
            type: "success",
            message: t(
              "contract:importer.detail.alert.confirmationRequestCompletedAlert",
            ),
          });
          break;
        case "confirmSc":
          if (data.signatureMediaId) {
            await confirmImporterSc({
              id,
              signatureMediaId: data.signatureMediaId,
            }).unwrap();
          }
          alert.showAlert({
            type: "success",
            message: t(
              "contract:importer.detail.alert.signatureCompletedAlert",
            ),
          });
          break;
        case "rejectSc":
          if (data.rejectReason) {
            await rejectSc({ id, rejectReason: data.rejectReason }).unwrap();
          }
          alert.showAlert({
            type: "success",
            message: t("contract:importer.detail.alert.rejectCompletedAlert"),
          });
          break;
      }
      await getContractDetail({
        id: Number(params.id),
      });
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    } finally {
      setDialogState(DialogState.NULL);
      setAlertDialogState(AlertDialogState.NULL);
      handleContentLoadingOff();
    }
  };

  const handleTabChange = (newTab: TabType) => {
    setTab(newTab);
    setSearchParams({ selectTab: newTab }, { replace: true });
  };

  const renderDialogs = () => {
    if (dialogState === DialogState.SEND_EMAIL) {
      return (
        <EmailDialog
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
        />
      );
    }
    if (dialogState === DialogState.SENT_HISTORY) {
      return (
        <EmailHistoryDialog
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
          isFetching={isEmailShareHistoriesFetching}
          isError={isEmailShareHistoriesError}
          emailHistoryList={emailHistoryList}
          totalCount={count}
          fetch={getContractSharedHistories}
          emailShareDomain={"CONTRACT"}
          emailShareDomainId={Number(params.id)}
        />
      );
    }
    if (dialogState === DialogState.REJECT_SC_REASON) {
      return (
        <RejectReasonDialog
          onClose={() => setDialogState(DialogState.NULL)}
          onSubmit={async (rejectReason) => {
            await handleDocument({ type: "rejectSc", rejectReason });
          }}
        />
      );
    }
    // 서명 등록 다이얼로그는 별도로 처리됨
    if (dialogState === DialogState.CONTRACT_HISTORY) {
      // 플로우, 단계, Lead 여부 계산
      const flow = determineContractFlow(
        signatureHistories?.rows,
        "IMPORTER_LEAD",
      );
      const _currentStage = determineContractStage(
        signatureHistories?.rows,
        flow,
      );
      const isLead = checkIsLeadRole(signatureHistories?.rows, "IMPORTER");

      // Format signature histories for display
      const formatSignatureHistory = (history: any) => {
        const getTitle = () => {
          // 문서 타입 판단 - 유틸리티 함수 사용
          const docType = getHistoryDocumentType(history, flow);

          // 반려 상태를 먼저 체크 (PENDING + rejectReason)
          if (history.signatureStatus === "PENDING" && history.rejectReason) {
            if (docType === "SC") {
              return t("contract:history.status.scRejected");
            } else if (docType === "PO") {
              return t("contract:history.status.poRejected");
            }
            return t("contract:history.status.poRejected"); // Default to PO for importer
          }

          // 일반 상태 처리
          switch (history.signatureStatus) {
            case "DRAFT":
              // 초안 생성
              if (docType === "SC") {
                return t("contract:history.status.scDraftCreated");
              } else if (docType === "PO") {
                return t("contract:history.status.poDraftCreated");
              }
              return t("contract:history.status.draftCreated");

            case "SENT":
              // 서명 요청 전송
              if (docType === "SC") {
                return t("contract:history.status.scSignatureRequested");
              } else if (docType === "PO") {
                return t("contract:history.status.poSignatureRequested");
              }
              return t("contract:history.status.signatureRequested");

            case "PENDING":
              // 서명 대기 중
              if (history.rejectReason) {
                // 반려인 경우 위에서 처리됨
                return t("contract:history.status.signaturePending");
              }
              if (docType === "SC") {
                return t("contract:history.status.scSignaturePending");
              } else if (docType === "PO") {
                return t("contract:history.status.poSignaturePending");
              }
              return t("contract:history.status.signaturePending");

            case "APPROVED":
              // 서명 완료
              if (docType === "SC") {
                return t("contract:history.status.scSignatureCompleted");
              } else if (docType === "PO") {
                return t("contract:history.status.poSignatureCompleted");
              }
              return t("contract:history.status.signatureCompleted");

            case "REQUESTED":
              // 서명 요청됨
              if (docType === "SC") {
                return t("contract:history.status.scSignatureRequested");
              } else if (docType === "PO") {
                return t("contract:history.status.poSignatureRequested");
              }
              return t("contract:history.status.signatureRequested");

            case "LINKED":
              return t("contract:history.status.contractLinked");

            case "REJECTED":
              // 반려 상태
              if (docType === "SC") {
                return t("contract:history.status.scRejected");
              } else if (docType === "PO") {
                return t("contract:history.status.poRejected");
              }
              return t("contract:history.status.poRejected"); // Default to PO for importer

            default:
              return t("contract:history.status.statusChanged");
          }
        };

        const getStatus = () => {
          if (history.rejectReason || history.signatureStatus === "REJECTED") {
            return "rejected" as const;
          }
          return "completed" as const;
        };

        return {
          id: history.id,
          status: getStatus(),
          title: getTitle(),
          user: history.editorName || "-",
          timestamp: dayjs(history.updatedAt).format("YY-MM-DD HH:mm"),
          rejectionReason: history.rejectReason,
          updatedAt: history.updatedAt, // 정렬을 위해 원본 날짜 보존
        };
      };

      const historyItems =
        signatureHistories?.rows
          ?.map(formatSignatureHistory)
          ?.filter((item, index) => {
            // Follow 역할이고 DRAFT 상태인 경우 필터링
            const originalHistory = signatureHistories?.rows?.[index];

            // Follow이고 DRAFT 상태인 경우 필터링
            if (!isLead && originalHistory?.signatureStatus === "DRAFT") {
              // 수입자 Follow인 경우 SC 초안은 보지 않음
              // SC 문서인지 확인 (exporterScSignMedia가 있고 importerPOSignMedia가 없는 경우)
              const isSCDraft =
                originalHistory.exporterScSignMedia &&
                !originalHistory.importerPOSignMedia;
              if (isSCDraft) {
                return false;
              }
            }
            return true;
          })
          ?.sort((a, b) => {
            // 원본 날짜를 비교하여 최신 것부터 정렬 (내림차순)
            const dateA = dayjs(a.updatedAt);
            const dateB = dayjs(b.updatedAt);
            return dateA.isBefore(dateB) ? 1 : dateA.isAfter(dateB) ? -1 : 0;
          }) || [];

      // Add contract completion status if contract is complete
      if (contractStatus === "COMPLETE" && historyItems.length > 0) {
        historyItems.unshift({
          id: 0,
          status: "completed" as const,
          title: t("contract:history.status.contractCompleted"),
          user: contractLastEditorName,
          timestamp: dayjs(contractUpdatedAt).format("YY-MM-DD HH:mm"),
          updatedAt: contractUpdatedAt,
          rejectionReason: undefined,
        });
      }

      return (
        <ContractHistoryCard
          historyItems={historyItems}
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
          modalTitle={t("contract:history.modalTitle")}
        />
      );
    }
    // 반려 사유 보기 모달
    if (dialogState === DialogState.VIEW_REJECT_REASON) {
      // 반려 사유 확인 버튼 표시 여부 결정
      // SC 반려: 수출자가 확인해야 함 (수입자 페이지에서 버튼 숨김)
      // PO 반려: 수입자가 확인해야 함 (수입자 페이지에서 버튼 표시)
      let hideEditButton = false;

      if (contractNoticeType?.includes("SC_REJECTED")) {
        hideEditButton = true; // SC 반려는 수출자가 확인
      }

      return (
        <RejectReasonViewModal
          isOpen={true}
          onClose={() => setDialogState(DialogState.NULL)}
          rejectReason={currentSignature?.rejectReason}
          title={t("contract:reject.viewTitle")}
          contractId={Number(params.id)}
          hideEditButton={hideEditButton}
        />
      );
    }
  };

  const getProgressInfo = () => {
    if (signatureType === "PO") {
      return {
        currentStep: t("contract:status.notice.poSignaturePending.title"),
        nextStep: t("contract:status.notice.contractComplete.title"),
      };
    } else {
      return {
        currentStep: t("contract:status.notice.scSignaturePending.title"),
        nextStep: t("contract:status.notice.contractComplete.title"),
      };
    }
  };

  const renderAlertDialogs = () => {
    const closeAlertDialog = () => setAlertDialogState(AlertDialogState.NULL);
    // 발주완료
    if (alertDialogState === AlertDialogState.CANCEL) {
      return (
        <CancelContractAlertDialog
          open
          onOpenChange={() => setAlertDialogState(AlertDialogState.NULL)}
          onOk={() => {
            setAlertDialogState(AlertDialogState.NULL);
            handleContractStatusChangeClick("COMPLETE");
          }}
        />
      );
    }
    // 발주 되돌리기
    if (alertDialogState === AlertDialogState.REDO) {
      return (
        <RedoAlertDialog
          open
          onOpenChange={() => setAlertDialogState(AlertDialogState.NULL)}
          onOk={() => {
            setAlertDialogState(AlertDialogState.NULL);
            handleContractStatusChangeClick("PROCESSING");
          }}
        />
      );
    }
    // SC서명 (1개일 경우)
    if (alertDialogState === AlertDialogState.SIGN_SC) {
      // PDF URL 가져오기
      (async () => {
        try {
          const contractId = Number(params.id);
          const scHistories = await getImporterScHistories({
            contractId,
          }).unwrap();
          const pdfUrl =
            scHistories?.rows?.[0]?.documentUrl ||
            scHistories?.rows?.[0]?.mediaFileUrl;
          if (pdfUrl) {
            setPdfUrl(pdfUrl);
            if (defaultSignatureMediaId) {
              setSelectedSignatureId(defaultSignatureMediaId);
            }
            setSignatureType("SC");
            setDialogState(DialogState.SIGNATURE_FULLSCREEN);
          }
        } catch (error) {
          console.error("Failed to fetch SC PDF URL:", error);
        }
      })();
      closeAlertDialog();
      return null;
    }
    // SC서명 서명선택 (서명 2개일 경우)
    if (alertDialogState === AlertDialogState.SIGNATURE_SELECT_SC) {
      return (
        <SignatureSelectDialog
          open
          onSave={async (selectedSignatureId) => {
            try {
              // 서명 선택 완료 시 바로 서명 처리
              setSelectedSignatureId(selectedSignatureId);
              closeAlertDialog();

              await handleDocument({
                type: "confirmSc",
                signatureMediaId: selectedSignatureId,
              });
              // 풀스크린도 함께 닫기
              setDialogState(DialogState.NULL);
            } catch (error) {
              console.error("Failed to process signature:", error);
            }
          }}
          onOpenChange={closeAlertDialog}
          onRegisterClick={() => {
            // 현재 알림 다이얼로그만 닫고 서명 등록 다이얼로그 열기
            setAlertDialogState(AlertDialogState.NULL);
            setShowRegisterSignDialog(true);
          }}
        />
      );
    }
    // SC반려
    if (alertDialogState === AlertDialogState.REJECT_SC) {
      return (
        <AlertDialog
          open
          onOpenChange={closeAlertDialog}
          title={t("contract:importer.detail.alertDialog.rejectContractTitle")}
          description={t(
            "contract:importer.detail.alertDialog.rejectContractDescription",
          )}
          onOk={async () => await handleDocument({ type: "rejectSc" })}
          onCancel={closeAlertDialog}
          cancelText={t("contract:importer.detail.button.cancelButton")}
          okText={t("contract:importer.detail.button.okButton")}
        />
      );
    }
    // PO컨펌요청 (서명 1개일 경우)
    if (alertDialogState === AlertDialogState.CONFIRM_REQUEST_PO) {
      if (defaultSignatureMediaId) {
        setSelectedSignatureId(defaultSignatureMediaId);
      }
      setSignatureType("PO");
      setDialogState(DialogState.SIGNATURE_FULLSCREEN);
      closeAlertDialog();
      return null;
    }
    // PO컨펌요청 (서명 2개일 경우)
    if (alertDialogState === AlertDialogState.SIGNATURE_SELECT_PO) {
      return (
        <SignatureSelectDialog
          open
          onSave={async (selectSignatureMediaId) => {
            try {
              // 서명 선택 완료 시 바로 서명 처리
              setSelectedSignatureId(selectSignatureMediaId);
              closeAlertDialog();

              await handleDocument({
                type: "confirmPo",
                signatureMediaId: selectSignatureMediaId,
              });
              // 풀스크린도 함께 닫기
              setDialogState(DialogState.NULL);
            } catch (error) {
              console.error("Failed to process signature:", error);
            }
          }}
          onOpenChange={closeAlertDialog}
          onRegisterClick={() => {
            // 현재 알림 다이얼로그만 닫고 서명 등록 다이얼로그 열기
            setAlertDialogState(AlertDialogState.NULL);
            setShowRegisterSignDialog(true);
          }}
        />
      );
    }
    // 서명등록
    if (alertDialogState === AlertDialogState.REGISTER_SIGN) {
      return (
        <NoRegisteredSignatureAlertDialog
          open
          onOpenChange={closeAlertDialog}
          onOk={() => {
            // 현재 알림 다이얼로그만 닫고 서명 등록 다이얼로그 열기
            setAlertDialogState(AlertDialogState.NULL);
            setShowRegisterSignDialog(true);
          }}
          onCancel={closeAlertDialog}
        />
      );
    }
    // 서명등록 요청
    if (alertDialogState === AlertDialogState.SIGNATURE_REQUEST) {
      return (
        <SignatureRequestAlertDialog
          open
          onOpenChange={closeAlertDialog}
          onCancel={closeAlertDialog}
        />
      );
    }
  };

  const renderUnauthorizedDescription = () => {
    if (isContractDetailFetching || isUserFetching) {
      return;
    }

    if (isContractUnstable || isMainCategoryUnauthorized) {
      return (
        <NoDataContainer>
          <UnauthorizedDescription />
        </NoDataContainer>
      );
    }
  };

  const _renderFooter = () => {
    switch (tab) {
      case "contractInformation":
        return null;
    }
  };

  const renderContentByTab = () => {
    switch (tab) {
      case "contractInformation":
        return (
          <ContractInformationTab
            isEditAuth={isEditAuth}
            balanceOfContractV2={balanceOfContractV2}
            percentOfShipment={percentOfShipment}
            totalShippingWeightV2={totalShippingWeightV2}
            emailDialogOn={() => setDialogState(DialogState.SEND_EMAIL)}
            historyDialogOn={() => setDialogState(DialogState.CONTRACT_HISTORY)}
            isOnlyReceivedSc={isOnlyReceivedSc}
            isDisabledEdit={isDisabledEdit}
            onContractNoticeAction={handleContractNoticeAction}
            rejectReason={currentSignature?.rejectReason}
            poNo={poNo}
            scNo={scNo}
            onSignatureRequired={handleSignatureRequired}
            onOpenSignatureViewer={async (type) => {
              try {
                const contractId = Number(params.id);
                let pdfUrl = "";

                if (type === "SC") {
                  const scHistories = await getImporterScHistories({
                    contractId,
                  }).unwrap();
                  pdfUrl =
                    scHistories?.rows?.[0]?.documentUrl ||
                    scHistories?.rows?.[0]?.mediaFileUrl;
                } else if (type === "PO") {
                  const poHistories = await getImporterPoHistories({
                    contractId,
                  }).unwrap();
                  pdfUrl =
                    poHistories?.rows?.[0]?.documentUrl ||
                    poHistories?.rows?.[0]?.mediaFileUrl;
                }

                if (pdfUrl) {
                  setPdfUrl(pdfUrl);
                  setSelectedSignatureId(null);
                  setSignatureType(type);
                  setDialogState(DialogState.SIGNATURE_FULLSCREEN);
                }
              } catch (error) {
                console.error(`Failed to fetch ${type} PDF URL:`, error);
              }
            }}
          />
        );
      case "processing":
        return (
          <ProcessingTab
            tab={processingTab}
            onTabChange={(tab) => {
              if (processingTab.includes(tab)) {
                setProcessingTab((prev) => prev.filter((item) => item !== tab));
              } else {
                setProcessingTab((prev) => [...prev, tab]);
              }
            }}
            hasLinkedBefore={!!hasLinkedBefore}
            signatureStatus={signatureStatus}
            selectedProcessingTab={selectedProcessingTab}
            selectedProcessingItem={selectedProcessingItem}
            onSelectedItemChange={(tab, item) => {
              setSelectedProcessingTab(tab);
              setSelectedProcessingItem(item);
            }}
          />
        );
      case "store":
        return <StoreTab />;
      case "files":
        return <ContractFilesTab tab={filesTab} onTabChange={setFilesTab} />;
    }
  };

  const renderContractDetailContent = () => {
    if (isContractUnstable || isMainCategoryUnauthorized) {
      return;
    }

    return (
      <>
        {/* Content */}
        <FlexColumn>
          {/* Header */}
          <HeaderContainer>
            <HeaderButtonContainer>
              {renderContractStatusBadge({
                t,
                type: contractStatus as ContractStatusType | null,
                size: "L",
              })}
            </HeaderButtonContainer>
          </HeaderContainer>

          <Tabs role="tablist">
            {TAB_LIST.map(({ id, label }) => {
              return (
                <StyledTabItem
                  key={id}
                  tabIndex={id === tab ? 0 : -1}
                  isSelect={id === tab}
                  tabValue={id}
                  onClick={() => handleTabChange(id)}
                  onFocusItem={(value) => handleTabChange(value as TabType)}
                >
                  <Typo typoType="b5m" color={id === tab ? "white" : "gray6"}>
                    {t(label)}
                  </Typo>
                </StyledTabItem>
              );
            })}
          </Tabs>
          {renderContentByTab()}
          {/* Last Editor */}
          <EditorContainer>
            <figure>
              <Icon iconSrc={DotGray7Svg} iconSize={4} />
              <Typo color="gray6" typoType="b9r">
                {`${t("contract:importer.detail.editor.registratant")} (${t(
                  "contract:importer.detail.editor.registratantAt",
                )}) ${contractWriterName}`}
              </Typo>
              <Typo color="gray7" typoType="b9r">
                {contractCreatedDate}
              </Typo>
            </figure>
            <figure>
              <Icon iconSrc={DotGray4Svg} iconSize={4} />
              <Typo color="gray4" typoType="b9r">
                {`${t("contract:importer.detail.editor.lastEditor")} (${t(
                  "contract:importer.detail.editor.lastEditorAt",
                )}) ${contractLastEditorName}`}
              </Typo>
              <Typo color="gray7" typoType="b9r">
                {contractUpdatedDate}
              </Typo>
            </figure>
          </EditorContainer>
        </FlexColumn>
      </>
    );
  };

  useEffect(() => {
    const fetchContractDetail = async (retryCount = 0) => {
      try {
        await getContractDetail({
          id: Number(params.id),
        }).unwrap();

        // TODO: 계약이 연결되지 않은 경우 PO 탭으로 전환하는 기능 필요
      } catch (e: any) {
        const message = Array.isArray(e.data.message)
          ? e.data.message[0]
          : e.data.message;
        if (e.data.statusCode === 404) {
          alert.showAlert({ type: "error", message });
          navigate(IMPORTER_PRIVATE_PATH.CONTRACT);
          return;
        }

        if (retryCount < 5) {
          handleContentLoadingOn();
          setTimeout(() => {
            fetchContractDetail(retryCount + 1);
          }, 1000);
        } else {
          handleContentLoadingOff();
          alert.showAlert({ type: "error", message });
        }
      }
    };

    fetchContractDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getContractDetail, params.id]);

  useEffect(() => {
    (async () => {
      const poResult = await getImporterPoHistories({
        contractId: Number(params.id),
      });
      const scResult = await getImporterScHistories({
        contractId: Number(params.id),
      });

      await getContractSignatureHistories({ id: Number(params.id) });

      // TODO: PO/SC 조회 오류 시 탭 전환 기능 필요
      if (!poVersion) {
        setPoVersion?.(poResult.data?.rows?.[0].id);
      }
      if (!scVersion) {
        setScVersion?.(scResult.data?.rows?.[0].id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  // URL에 selectTab이 없으면 기본값 설정
  useEffect(() => {
    if (!searchParams.get("selectTab")) {
      setSearchParams({ selectTab: "contractInformation" }, { replace: true });
      setTab("contractInformation");
    }
  }, [searchParams, setSearchParams, params.id]);

  // PDF URL 가져오기
  useEffect(() => {
    const fetchPdfUrl = async () => {
      if (dialogState === DialogState.SIGNATURE_FULLSCREEN && params.id) {
        try {
          const contractId = Number(params.id);
          if (signatureType === "PO") {
            const poHistories = await getImporterPoHistories({
              contractId,
            }).unwrap();
            const pdfUrl =
              poHistories?.rows?.[0]?.documentUrl ||
              poHistories?.rows?.[0]?.mediaFileUrl;
            if (pdfUrl) {
              setPdfUrl(pdfUrl);
            }
          } else if (signatureType === "SC") {
            const scHistories = await getImporterScHistories({
              contractId,
            }).unwrap();
            const pdfUrl =
              scHistories?.rows?.[0]?.documentUrl ||
              scHistories?.rows?.[0]?.mediaFileUrl;
            if (pdfUrl) {
              setPdfUrl(pdfUrl);
            }
          }
        } catch (error) {
          console.error("Failed to fetch PDF URL:", error);
        }
      }
    };

    fetchPdfUrl();
  }, [
    dialogState,
    signatureType,
    params.id,
    getImporterPoHistories,
    getImporterScHistories,
  ]);

  return (
    <>
      <ImporterMainLayout
        breadcrumb={
          isContractStable && isMainCategoryAuthorized
            ? [t("sideNav:importContract"), t("sideNav:importContractDetail")]
            : []
        }
        pageTitle={
          <>
            <PageTitleContainer>
              {isContractStable &&
                isMainCategoryAuthorized &&
                `${t("contract:importer.detail.header.title")} ${poNo ?? "-"}`}
              <HeaderButtonContainer>
                <BookMarkStatus
                  bookMarkStatus={isBookmarked}
                  onClickBookMark={handleBookmarkClick}
                />
                <EmailDropdown
                  disabled={!isEditAuth || isZeroPo}
                  onEmailClick={() => setDialogState(DialogState.SEND_EMAIL)}
                  onHistoryClick={() =>
                    setDialogState(DialogState.SENT_HISTORY)
                  }
                  buttonText={t("contract:importer.detail.button.emailButton")}
                  sendEmailText={t(
                    "contract:importer.detail.button.sendEmailButton",
                  )}
                  sentHistoryText={t(
                    "contract:importer.detail.button.sentHistoryButton",
                  )}
                />
              </HeaderButtonContainer>
            </PageTitleContainer>
            {isMainCategoryAuthorized && <ClientName>{clientName}</ClientName>}
          </>
        }
      >
        {/* Contents */}
        {renderContractDetailContent()}
        {renderUnauthorizedDescription()}
        {/* Dialogs , AlertDialogs*/}
        {renderDialogs()}
        {renderAlertDialogs()}
      </ImporterMainLayout>
      {/* SignatureFullscreenViewer를 레이아웃 밖에서 렌더링 */}
      {dialogState === DialogState.SIGNATURE_FULLSCREEN &&
        pdfUrl &&
        signatureType && (
          <SignatureFullscreenViewer
            isOpen={true}
            onClose={() => {
              setDialogState(DialogState.NULL);
              setPdfUrl(null);
              setSelectedSignatureId(null);
              setSignatureType(null);
            }}
            pdfUrl={pdfUrl}
            contractNo={signatureType === "PO" ? poNo || "" : scNo || ""}
            userType="IMPORTER"
            onSign={async () => {
              // 서명이 선택되지 않았으면 서명 선택 프로세스 시작
              if (!selectedSignatureId) {
                if (!hasSign) {
                  setAlertDialogState(AlertDialogState.REGISTER_SIGN);
                } else if (isTwoSign) {
                  setAlertDialogState(
                    signatureType === "PO"
                      ? AlertDialogState.SIGNATURE_SELECT_PO
                      : AlertDialogState.SIGNATURE_SELECT_SC,
                  );
                } else {
                  // 서명이 1개인 경우 바로 서명 처리
                  if (defaultSignatureMediaId) {
                    await handleDocument({
                      type: signatureType === "PO" ? "confirmPo" : "confirmSc",
                      signatureMediaId: defaultSignatureMediaId,
                    });
                  }
                }
              } else {
                await handleDocument({
                  type: signatureType === "PO" ? "confirmPo" : "confirmSc",
                  signatureMediaId: selectedSignatureId,
                });
              }
            }}
            onReject={async (reason) => {
              if (signatureType === "SC" && reason) {
                await handleDocument({
                  type: "rejectSc",
                  rejectReason: reason,
                });
              }
            }}
            currentStep={getProgressInfo().currentStep}
            nextStep={getProgressInfo().nextStep}
          />
        )}
      {/* 서명 등록 다이얼로그 - 독립적으로 렌더링 */}
      {showRegisterSignDialog && (
        <RegisterSignDialog
          id={Number(params.id)}
          onClose={() => setShowRegisterSignDialog(false)}
          signatureType={signatureType || "SC"}
          onSuccess={() => {
            // 서명 등록 성공 시 처리
            alert.showAlert({
              type: "success",
              message: t(
                "contract:importer.detail.alert.signatureCompletedAlert",
              ),
            });
            // 서명 등록 모달만 닫기
            setShowRegisterSignDialog(false);
            // PDF 뷰어가 열려있는 경우 해당 처리만 수행
            if (dialogState === DialogState.SIGNATURE_FULLSCREEN) {
              // 풀스크린 뷰어 닫기
              setDialogState(DialogState.NULL);
              setPdfUrl(null);
              setSelectedSignatureId(null);
              setSignatureType(null);
            }
            // 계약 상세 정보 다시 가져오기
            getContractDetail({
              id: Number(params.id),
            });
          }}
        />
      )}
    </>
  );
};

export default ImporterContractDetailPage;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderButtonContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const _StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 72px;
`;

const _BottomButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  div {
    display: flex;
    gap: 8px;
  }
`;

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  figure {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const NoDataContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ClientName = styled(Typo).attrs({
  as: "h5",
})`
  padding-top: 8px;
  color: ${colorSet.gray1};
  ${typo.b5m}
`;

const PageTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const Tabs = styled.div`
  display: flex;
  border-radius: 8px;
  box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.05);
`;

const StyledTabItem = styled(TabItem)<{
  isSelect?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: 8px 16px;
  border: none;
  border-right: 1px solid ${colorSet.gray9};
  cursor: pointer;
  background: ${({ isSelect }) =>
    isSelect ? colorSet.indigo : colorSet.gray11};
  border: 1px solid ${colorSet.gray9};

  &:last-child {
    border-right: none;
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  &:first-child {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }
`;
